import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  IconButton,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import CloseIcon from "@mui/icons-material/Close";
import ImageIcon from "@mui/icons-material/Image";
import { getAllMonthlyTargetsAction, getAllActivitiesAction, getActivityByIdAction, getAllMediaFilesAction, getAllSeasonsAction, getAllDistrictsAction, getAllMandalsAction } from "../../../store/actions";

const ViewPreActivity = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const preActivityData = location.state?.preActivityData;
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.only("xs"));

  const [expandedMonthId, setExpandedMonthId] = useState(null);

  const { successData: monthlyTargetsData, loading: monthlyTargetsLoading } = useSelector(
    (state) => state.getAllMonthlyTargetsReducer || {}
  );
  const { successData: activitiesData, loading: activitiesLoading } = useSelector(
    (state) => state.getAllActivitiesReducer || {}
  );
  const { successData: activityByIdData, loading: activityByIdLoading } = useSelector(
    (state) => state.getActivityByIdReducer || {}
  );
  const { successData: mediaFilesData, loading: mediaFilesLoading } = useSelector(
    (state) => state.getAllMediaFilesReducer || {}
  );
  const { successData: seasons } = useSelector((state) => state.getAllSeasonsReducer || {});
  const seasonList = seasons?.data?.seasons || [];
  
  // Get districts and mandals from Redux
  const { successData: districtsData } = useSelector((state) => state.getAllDistrictsReducer || {});
  const { successData: mandalsData } = useSelector((state) => state.getAllMandalsReducer || {});
  const districtList = districtsData?.data?.districts || [];
  const mandalList = mandalsData?.data?.mandals || [];
    // State for image dialog
    const [openImageDialog, setOpenImageDialog] = useState(false);

    // Open image dialog and dispatch action
    const handleOpenImages = (activityId) => {
      setOpenImageDialog(true);
      dispatch(getAllMediaFilesAction({
        endPoint: `activity-media/pre?activityId=${activityId}`
      }));
    };

    // Close image dialog
    const handleCloseImages = () => {
      setOpenImageDialog(false);
    };
  // Handle navigation to edit page after fetching activity by ID
  useEffect(() => {
    if (activityByIdData?.data) {
      navigate('/SOorMDO/createActivity', { state: { editData: activityByIdData.data } });
    }
  }, [activityByIdData, navigate]);

  // Edit icon click handler: fetch activity by ID
  const handleEditActivity = (activityId) => {
    
    // Use SOMDO endpoint for get by id
    dispatch(getActivityByIdAction({ endPoint: `activity/pre/${activityId}` }));
  };

  useEffect(() => {
    dispatch(getAllSeasonsAction());
    dispatch(getAllDistrictsAction());
    dispatch(getAllMandalsAction());
    if (preActivityData) {
      dispatch(getAllMonthlyTargetsAction());
      dispatch(getAllActivitiesAction());
    }
  }, [dispatch, preActivityData]);

  // Filter monthly targets by pre_target_id and show all months
  const filteredMonthlyTargets = useMemo(() => {
    if (!preActivityData || !monthlyTargetsData) return [];
    let monthlyTargetsList = monthlyTargetsData?.data || [];
    if (!Array.isArray(monthlyTargetsList)) return [];
    return monthlyTargetsList.filter((target) => {
      const targetPreTargetId = target.pre_target_id ?? target.preTargetId ?? target.preTarget?.id;
      return String(targetPreTargetId) === String(preActivityData.id);
    });
  }, [preActivityData, monthlyTargetsData]);

  // Get current year and month in YYYY-MM format
  const now = new Date();
  const currentMonthYear = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

  // Filter activities by pre_target_id and pre_month_id
  const getActivitiesForMonth = (monthId) => {
    if (!activitiesData) return [];
    
    let activitiesList = activitiesData?.activities || activitiesData?.data?.activities || activitiesData?.data || [];
    
    if (!Array.isArray(activitiesList)) {
      return [];
    }
    
    return activitiesList.filter((activity) => {
      const activityPreTargetId = activity.preTargetId ?? activity.pre_target_id;
      const activityPreMonthId = activity.preMonthId ?? activity.pre_month_id;
      return (
        String(activityPreTargetId) === String(preActivityData.id) &&
        String(activityPreMonthId) === String(monthId)
      );
    });
  };

  const handleToggleActivities = (monthId) => {
    setExpandedMonthId(expandedMonthId === monthId ? null : monthId);
  };

  const seasonObj = seasonList.find(s => s.seasonId === preActivityData?.season_id);
  const seasonDisplay = seasonObj ? `${seasonObj.name} (${seasonObj.year})` : preActivityData?.season_id;
  
  // Get area name based on area type
  const getAreaName = () => {
    if (!preActivityData) return '';
    if (preActivityData.target_area_type === 'mandal') {
      const mandal = mandalList.find(m => m.mandalId === preActivityData.target_area_id || m.id === preActivityData.target_area_id);
      const district = districtList.find(d => d.districtId === mandal?.districtId || d.id === mandal?.districtId);
      return district && mandal ? `${district.name} - ${mandal.name}` : mandal ? mandal.name : preActivityData.target_area_id;
    } else if (preActivityData.target_area_type === 'district') {
      const district = districtList.find(d => d.districtId === preActivityData.target_area_id || d.id === preActivityData.target_area_id);
      return district ? district.name : preActivityData.target_area_id;
    }
    return preActivityData.target_area_id;
  };

  if (!preActivityData) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography variant="h6" color="textSecondary">
          No pre-season activity data found.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", p: 3,mt:{xs:7,sm:0} }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={600}>
          Pre-Season Activity Details
        </Typography>
        <IconButton onClick={() => navigate(-1)}>
          <CloseIcon />
        </IconButton>
      </Box>


      {/* Pre-Season Activity Details Card - Responsive for xs */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3, position: "relative" }}>
        {isXs ? (
          <Box sx={{ p: 1, minWidth: 220, minHeight: 180, position: "relative" }}>
            {/* Status Chip in top right */}
            <Chip
              label={preActivityData?.is_active ? "Active" : "Inactive"}
              size="small"
              sx={{
                position: "absolute",
                top: 12,
                right: 12,
                backgroundColor: preActivityData?.is_active ? '#D1FAE5' : '#FECACA',
                color: preActivityData?.is_active ? '#047857' : '#B91C1C',
                fontWeight: 600,
                fontSize: 12,
              }}
            />
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                <span style={{ color: "#374151" }}>Activity ID:</span> {preActivityData?.id}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                <span style={{ color: "#374151" }}>Season:</span> {seasonDisplay}
              </Typography>
              <Typography variant="body2">
                <span style={{ color: "#374151" }}>Area Type:</span> {preActivityData?.target_area_type}
              </Typography>
              <Typography variant="body2">
                <span style={{ color: "#374151" }}>Area Name:</span> {getAreaName()}
              </Typography>
              <Typography variant="body2">
                <span style={{ color: "#374151" }}>VLM:</span> {preActivityData?.vlm_done_count}/{preActivityData?.vlm_count}
              </Typography>
              <Typography variant="body2">
                <span style={{ color: "#374151" }}>MVLM:</span> {preActivityData?.mvlm_done_count}/{preActivityData?.mvlm_count}
              </Typography>
              <Typography variant="body2">
                <span style={{ color: "#374151" }}>Farmer Target:</span> {preActivityData?.farmers_attended || 0}/{preActivityData?.farmers_required || 0}
              </Typography>
            </Box>
          </Box>
        ) : (
          <>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Activity Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" color="textSecondary">Activity ID</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>{preActivityData?.id}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" color="textSecondary">Season</Typography>
                <Typography variant="body1" sx={{ fontWeight: 400 }}>{seasonDisplay}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" color="textSecondary">Target Area Type</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>{preActivityData?.target_area_type}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" color="textSecondary">Target Area Name</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>{getAreaName()}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" color="textSecondary">VLM</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>{preActivityData?.vlm_done_count}/{preActivityData?.vlm_count}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" color="textSecondary">MVLM</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>{preActivityData?.mvlm_done_count}/{preActivityData?.mvlm_count}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" color="textSecondary">FARMER TARGET</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>{preActivityData?.farmers_attended || 0}/{preActivityData?.farmers_required || 0}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" color="textSecondary">Status</Typography>
                <Chip
                  label={preActivityData?.is_active ? "Active" : "Inactive"}
                  size="small"
                  sx={{
                    mt: 0.5,
                    backgroundColor: preActivityData?.is_active ? '#D1FAE5' : '#FECACA',
                    color: preActivityData?.is_active ? '#047857' : '#B91C1C',
                    fontWeight: 600
                  }}
                />
              </Grid>
            </Grid>
          </>
        )}
      </Paper>

      {/* Monthly Targets Section */}
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        Monthly Targets
      </Typography>
      {monthlyTargetsLoading ? (
        <Typography>Loading monthly targets...</Typography>
      ) : filteredMonthlyTargets.length > 0 ? (
        <Box>
          {filteredMonthlyTargets.map((target) => {
            const activities = getActivitiesForMonth(target.id);
            const isExpanded = expandedMonthId === target.id;
            const monthYear = target.month_year || target.monthYear;
            const showAddActivity = String(monthYear) === currentMonthYear;
            return (
              <Paper 
                key={target.id} 
                elevation={0} 
                sx={{ 
                  mb: 2, 
                  borderRadius: 3, 
                  border: "1px solid #E5E7EB",
                  overflow: "hidden"
                }}
              >
                {/* Monthly Target Row - Responsive for xs */}
                {isXs ? (
                  <Box sx={{ p: 1, minWidth: 200, minHeight: 120, position: "relative" }}>
                    {/* Status Chip in top right */}
                    <Chip
                      label={target.is_active || target.isActive ? "Active" : "Inactive"}
                      size="small"
                      sx={{
                        position: "absolute",
                        top: 10,
                        right: 10,
                        backgroundColor: (target.is_active || target.isActive) ? '#D1FAE5' : '#FECACA',
                        color: (target.is_active || target.isActive) ? '#047857' : '#B91C1C',
                        fontWeight: 600,
                        fontSize: 11,
                      }}
                    />
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        <span style={{ color: "#374151" }}>Month:</span> {monthYear}
                      </Typography>
                      <Typography variant="body2">
                        <span style={{ color: "#374151" }}>VLM:</span> {target.vlm_done_count ?? target.vlmDoneCount ?? 0}/{target.vlm_count ?? target.vlmCount ?? 0}
                      </Typography>
                      <Typography variant="body2">
                        <span style={{ color: "#374151" }}>MVLM:</span> {target.mvlm_done_count ?? target.mvlmDoneCount ?? 0}/{target.mvlm_count ?? target.mvlmCount ?? 0}
                      </Typography>
                    </Box>
                    {/* Action: Add Activity only for current month, View Activities for all */}
                    <Box sx={{ position: "absolute", bottom: 10, right: 10, display: "flex", flexDirection: "row", gap: 0.5, alignItems: "center" }}>
                      {showAddActivity && (
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          sx={{ borderRadius: 2, fontSize: 11, py: 0.3, px: 1 }}
                          onClick={() => navigate('/SOorMDO/createActivity', { state: { monthId: target.id, preTargetId: preActivityData.id } })}
                        >
                          Add Activity
                        </Button>
                      )}
                      {activities.length > 0 && (
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          sx={{ borderRadius: 2, fontSize: 11, py: 0.3, px: 1 }}
                          onClick={() => handleToggleActivities(target.id)}
                        >
                          {isExpanded ? "Hide" : "View Activities"}
                        </Button>
                      )}
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}>
                    <Box sx={{ flex: 1, display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 2, alignItems: "center" }}>
                      <Box>
                        <Typography variant="caption" color="textSecondary">Month ID</Typography>
                        <Typography variant="body2" fontWeight={600}>{target.id}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="textSecondary">Month</Typography>
                        <Typography variant="body2" fontWeight={600}>{monthYear}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="textSecondary">VLM</Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {target.vlm_done_count ?? target.vlmDoneCount ?? 0}/{target.vlm_count ?? target.vlmCount ?? 0}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="textSecondary">MVLM</Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {target.mvlm_done_count ?? target.mvlmDoneCount ?? 0}/{target.mvlm_count ?? target.mvlmCount ?? 0}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="textSecondary">Status</Typography>
                        <Chip
                          label={target.is_active || target.isActive ? "Active" : "Inactive"}
                          size="small"
                          sx={{
                            backgroundColor: (target.is_active || target.isActive) ? '#D1FAE5' : '#FECACA',
                            color: (target.is_active || target.isActive) ? '#047857' : '#B91C1C',
                            fontWeight: 600,
                            fontSize: 11
                          }}
                        />
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "row", gap: 0.5, alignItems: "center" }}>
                      {showAddActivity && (
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          sx={{ borderRadius: 2, fontSize: 11, py: 0.4, px: 1.5 }}
                          onClick={() => navigate('/SOorMDO/createActivity', { state: { monthId: target.id, preTargetId: preActivityData.id } })}
                        >
                          Add Activity
                        </Button>
                      )}
                      {activities.length > 0 && (
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          sx={{ borderRadius: 2, fontSize: 11, py: 0.4, px: 1.5 }}
                          onClick={() => handleToggleActivities(target.id)}
                        >
                          {isExpanded ? "Hide" : "View Activities"}
                        </Button>
                      )}
                    </Box>
                  </Box>
                )}

                {/* Activities Table - Collapsible */}
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <Box sx={{ px: 2, pb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: "#4B5563" }}>
                      Associated Activities
                    </Typography>
                    {activitiesLoading ? (
                      <Typography variant="body2" color="textSecondary">Loading activities...</Typography>
                    ) : activities.length > 0 ? (
                      isXs ? (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                          {activities.map((activity) => (
                            <Paper
                              key={activity.id}
                              elevation={0}
                              sx={{
                                border: "1px solid #E5E7EB",
                                borderRadius: 2,
                                p: 1.5,
                                position: "relative",
                                minWidth: 200,
                                minHeight: 110,
                              }}
                            >
                              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  <span style={{ color: "#374151" }}>Activity Name:</span> {activity.activityName || activity.activity_name}
                                </Typography>
                                <Typography variant="body2">
                                  <span style={{ color: "#374151" }}>Village:</span> {activity?.village?.name}
                                </Typography>
                                <Typography variant="body2">
                                  <span style={{ color: "#374151" }}>Start Time:</span> {new Date(activity.startTime || activity.start_time).toLocaleString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </Typography>
                                <Typography variant="body2">
                                  <span style={{ color: "#374151" }}>End Time:</span> {new Date(activity.endTime || activity.end_time).toLocaleString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </Typography>
                                <Typography variant="body2">
                                  <span style={{ color: "#374151" }}>Farmers:</span> {activity.noOfFarmers || activity.no_of_farmers}
                                </Typography>
                              </Box>
                              <Box sx={{ position: "absolute", bottom: 8, right: 8, display: "flex", gap: 1 }}>
                                <IconButton
                                  color="primary"
                                  size="small"
                                  onClick={() => handleOpenImages(activity.id)}
                                  disabled={mediaFilesLoading}
                                >
                                  <ImageIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </Paper>
                          ))}
                        </Box>
                      ) : (
                        <Paper elevation={0} sx={{ border: "1px solid #E5E7EB", borderRadius: 2 }}>
                          <Table size="small">
                            <TableHead sx={{ backgroundColor: "#F9FAFB" }}>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Activity ID</TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Village</TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Activity Name</TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Start Time</TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>End Time</TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>No. of Farmers</TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Actions</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {activities.map((activity) => (
                                <TableRow key={activity.id}>
                                  <TableCell>{activity.id}</TableCell>
                                  <TableCell>{activity?.village?.name}</TableCell>
                                  <TableCell>{activity.activityName || activity.activity_name}</TableCell>
                                  <TableCell>
                                    {new Date(activity.startTime || activity.start_time).toLocaleString('en-US', {
                                      year: 'numeric',
                                      month: 'short',
                                      day: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </TableCell>
                                  <TableCell>
                                    {new Date(activity.endTime || activity.end_time).toLocaleString('en-US', {
                                      year: 'numeric',
                                      month: 'short',
                                      day: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </TableCell>
                                  <TableCell>{activity.noOfFarmers || activity.no_of_farmers}</TableCell>
                                  <TableCell>
                                    <Box sx={{ display: "flex", gap: 1 }}>
                                      <IconButton
                                        color="primary"
                                        size="small"
                                        onClick={() => handleOpenImages(activity.id)}
                                        disabled={mediaFilesLoading}
                                      >
                                        <ImageIcon fontSize="small" />
                                      </IconButton>
                                            {/* Image Dialog */}
                                            <Dialog open={openImageDialog} onClose={handleCloseImages} maxWidth="md" fullWidth>
                                              <DialogTitle>Activity Images</DialogTitle>
                                              <DialogContent dividers>
                                                {mediaFilesLoading ? (
                                                  <Typography>Loading images...</Typography>
                                                ) : mediaFilesData && Array.isArray(mediaFilesData.data?.media) && mediaFilesData.data.media.length > 0 ? (
                                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                                    {mediaFilesData.data.media.map((img, idx) => (
                                                      <Box
                                                        key={img.id || idx}
                                                        sx={{ width: 180, height: 180, borderRadius: 2, overflow: 'hidden', border: '1px solid #eee', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa', cursor: 'pointer' }}
                                                        onClick={() => window.open(img.filePath, '_blank', 'noopener,noreferrer')}
                                                      >
                                                        <img
                                                          src={img.filePath}
                                                          alt={img.activity?.activity_name || `Activity Image ${idx + 1}`}
                                                          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                                        />
                                                      </Box>
                                                    ))}
                                                  </Box>
                                                ) : (
                                                  <Typography color="textSecondary">No images found for this activity.</Typography>
                                                )}
                                              </DialogContent>
                                              <DialogActions>
                                                <Button onClick={handleCloseImages} color="primary" variant="contained">Close</Button>
                                              </DialogActions>
                                            </Dialog>
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Paper>
                      )
                    ) : (
                      <Typography variant="body2" color="textSecondary" sx={{ py: 2, textAlign: "center" }}>
                        No activities found.
                      </Typography>
                    )}
                  </Box>
                </Collapse>
              </Paper>
            );
          })}
        </Box>
      ) : (
        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #E5E7EB", textAlign: "center" }}>
          <Typography color="textSecondary">No monthly targets found for this pre-season activity.</Typography>
        </Paper>
      )}

      {/* Back Button */}
      <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          color="primary"
          sx={{ borderRadius: 2 }}
          onClick={() => navigate(-1)}
        >
          Back to Pre-Activities
        </Button>
      </Box>
    </Box>
  );
};

export default ViewPreActivity;
